import { useCallback, useEffect, useMemo, useState } from 'react'
import { Building2, Edit2, Plus, Search, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Badge } from 'shared/ui/badge'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { DEFAULT_TENANT_ID } from 'shared/utils/tenantContext'
import {
  CompanyDto,
  CompanyStatusFilter,
  CompanyUpsertRequest,
  createCompany,
  deleteCompany,
  searchCompanies,
  updateCompany,
} from 'modules/admin/api/companyService'

interface CompanyMasterProps {
  currentUser?: {
    tenantId?: string
    name?: string
  }
}

interface CompanyFormState {
  type: string
  code: string
  name: string
  taxId: string
  phone: string
  email: string
  isActive: boolean
}

const COMPANY_TYPES = [
  { value: 'CONTRACTOR', label: 'ผู้รับเหมา' },
  { value: 'OWNER', label: 'Owner' },
  { value: 'SUPPLIER', label: 'Supplier' },
]

const initialFormState: CompanyFormState = {
  type: COMPANY_TYPES[0]?.value ?? 'CONTRACTOR',
  code: '',
  name: '',
  taxId: '',
  phone: '',
  email: '',
  isActive: true,
}

export function CompanyMaster({ currentUser }: CompanyMasterProps) {
  const tenantId = currentUser?.tenantId ?? DEFAULT_TENANT_ID

  const [companies, setCompanies] = useState<CompanyDto[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<CompanyStatusFilter>('all')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const [formState, setFormState] = useState<CompanyFormState>(initialFormState)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingCompany, setEditingCompany] = useState<CompanyDto | null>(null)

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))

  const loadCompanies = useCallback(async () => {
    if (!tenantId) {
      return
    }
    setIsLoading(true)
    try {
      const response = await searchCompanies({
        tenantId,
        search: searchTerm.trim() || undefined,
        status: statusFilter,
        page,
        pageSize,
      })
      setCompanies(response.items)
      setTotalItems(response.totalItems)
    } catch (error) {
      console.error(error)
      toast.error('ไม่สามารถโหลดข้อมูลบริษัทได้')
    } finally {
      setIsLoading(false)
    }
  }, [page, pageSize, searchTerm, statusFilter, tenantId])

  useEffect(() => {
    setPage(1)
  }, [searchTerm, statusFilter])

  useEffect(() => {
    loadCompanies()
  }, [loadCompanies])

  const resetForm = () => {
    setFormState(initialFormState)
    setEditingCompany(null)
  }

  const handleDialogChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) {
      resetForm()
    }
  }

  const handleOpenCreate = () => {
    resetForm()
    setIsDialogOpen(true)
  }

  const handleOpenEdit = (company: CompanyDto) => {
    setEditingCompany(company)
    setFormState({
      type: company.type || initialFormState.type,
      code: company.code,
      name: company.name,
      taxId: company.taxId ?? '',
      phone: company.phone ?? '',
      email: company.email ?? '',
      isActive: company.isActive,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!tenantId) {
      toast.error('ไม่พบข้อมูล Tenant ID')
      return
    }

    if (!formState.code.trim() || !formState.name.trim()) {
      toast.error('กรุณากรอกรหัสและชื่อบริษัท')
      return
    }

    const payload: CompanyUpsertRequest = {
      tenantId,
      code: formState.code.trim(),
      name: formState.name.trim(),
      type: formState.type,
      taxId: formState.taxId.trim() || undefined,
      phone: formState.phone.trim() || undefined,
      email: formState.email.trim() || undefined,
      isActive: formState.isActive,
    }

    setIsSubmitting(true)
    try {
      if (editingCompany) {
        await updateCompany(editingCompany.companyId, payload)
        toast.success('อัปเดตข้อมูลบริษัทสำเร็จ')
      } else {
        await createCompany(payload)
        toast.success('เพิ่มบริษัทใหม่สำเร็จ')
      }
      handleDialogChange(false)
      await loadCompanies()
    } catch (error) {
      console.error(error)
      toast.error('บันทึกข้อมูลบริษัทไม่สำเร็จ')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (company: CompanyDto) => {
    if (!tenantId) {
      toast.error('ไม่พบข้อมูล Tenant ID')
      return
    }

    const confirmed = window.confirm(`ต้องการลบบริษัท ${company.name} หรือไม่?`)
    if (!confirmed) {
      return
    }

    try {
      await deleteCompany(company.companyId, tenantId)
      toast.success('ลบบริษัทสำเร็จ')
      await loadCompanies()
    } catch (error) {
      console.error(error)
      toast.error('ไม่สามารถลบบริษัทได้')
    }
  }

  const tableBody = useMemo(() => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-6 text-center text-sm text-gray-500">
            กำลังโหลดข้อมูล...
          </TableCell>
        </TableRow>
      )
    }

    if (companies.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-6 text-center text-sm text-gray-500">
            ไม่พบข้อมูลบริษัท
          </TableCell>
        </TableRow>
      )
    }

    return companies.map((company) => (
      <TableRow key={company.companyId}>
        <TableCell>
          <div className="font-semibold text-gray-900">{company.name}</div>
          <div className="text-xs text-gray-500">{company.code}</div>
        </TableCell>
        <TableCell className="text-sm text-gray-600">{company.type}</TableCell>
        <TableCell>
          <div className="text-sm text-gray-900">{company.phone ?? '-'}</div>
          <div className="text-xs text-gray-500">{company.email ?? '-'}</div>
        </TableCell>
        <TableCell className="text-sm text-gray-600">{company.taxId ?? '-'}</TableCell>
        <TableCell>
          <Badge variant={company.isActive ? 'default' : 'secondary'} className="text-xs">
            {company.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </TableCell>
        <TableCell className="text-right space-x-2">
          <Button variant="outline" size="icon" onClick={() => handleOpenEdit(company)}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleDelete(company)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </TableCell>
      </TableRow>
    ))
  }, [companies, handleDelete, handleOpenEdit, isLoading])

  const dialogTitle = editingCompany ? 'แก้ไขข้อมูลบริษัท' : 'เพิ่มบริษัทใหม่'

  return (
    <>
      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Building2 className="h-5 w-5 text-blue-600" />
                Company Master
              </CardTitle>
              <p className="text-sm text-gray-500">
                จัดการข้อมูลบริษัท (บันทึกลงตาราง ptw.companies เท่านั้น ไม่เชื่อมโยงข้อมูลอื่น)
              </p>
            </div>
            <Button onClick={handleOpenCreate}>
              <Plus className="mr-2 h-4 w-4" />
              เพิ่มบริษัท
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative w-full md:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="ค้นหาด้วยชื่อหรือรหัสบริษัท"
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={(value: CompanyStatusFilter) => setStatusFilter(value)}>
                <SelectTrigger className="md:w-48">
                  <SelectValue placeholder="สถานะ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทั้งหมด</SelectItem>
                  <SelectItem value="active">ใช้งาน</SelectItem>
                  <SelectItem value="inactive">ไม่ใช้งาน</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ชื่อบริษัท</TableHead>
                    <TableHead>ประเภท</TableHead>
                    <TableHead>ติดต่อ</TableHead>
                    <TableHead>เลขภาษี</TableHead>
                    <TableHead>สถานะ</TableHead>
                    <TableHead className="text-right">ดำเนินการ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{tableBody}</TableBody>
              </Table>
            </div>

            <div className="flex flex-col gap-2 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
              <span>
                แสดง {companies.length} รายการ จากทั้งหมด {totalItems} รายการ
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
                  ก่อนหน้า
                </Button>
                <span>
                  หน้า {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                >
                  ถัดไป
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>บันทึกข้อมูลบริษัทลงตาราง ptw.companies</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>ประเภทบริษัท</Label>
                <Select value={formState.type} onValueChange={(value) => setFormState((prev) => ({ ...prev, type: value }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="เลือกประเภท" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_TYPES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>รหัสบริษัท</Label>
                <Input
                  className="mt-1"
                  value={formState.code}
                  onChange={(event) => setFormState((prev) => ({ ...prev, code: event.target.value }))}
                  placeholder="เช่น ABC-001"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>ชื่อบริษัท</Label>
                <Input
                  className="mt-1"
                  value={formState.name}
                  onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="ชื่อเต็มของบริษัท"
                />
              </div>
              <div>
                <Label>เลขที่ผู้เสียภาษี</Label>
                <Input
                  className="mt-1"
                  value={formState.taxId}
                  onChange={(event) => setFormState((prev) => ({ ...prev, taxId: event.target.value }))}
                  placeholder="เลข 13 หลัก"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>เบอร์โทร</Label>
                <Input
                  className="mt-1"
                  value={formState.phone}
                  onChange={(event) => setFormState((prev) => ({ ...prev, phone: event.target.value }))}
                  placeholder="02-xxx-xxxx"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  className="mt-1"
                  type="email"
                  value={formState.email}
                  onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                  placeholder="contact@example.com"
                />
              </div>
            </div>

            <div>
              <Label>สถานะการใช้งาน</Label>
              <Select
                value={formState.isActive ? 'active' : 'inactive'}
                onValueChange={(value) => setFormState((prev) => ({ ...prev, isActive: value === 'active' }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="เลือกสถานะ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">ใช้งาน</SelectItem>
                  <SelectItem value="inactive">ไม่ใช้งาน</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => handleDialogChange(false)}>
              ยกเลิก
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CompanyMaster
